import { ContextMenuItem, ContextMenuSeparator } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useCreateMatterFileReference } from "@/lib/data/react-query/matters/matter-file";
import type { MATTER_TEMPLATE_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  Matter,
  MatterDocumentSubItem,
  MatterSubItemType,
} from "@/lib/types";
import { getMatterFileReferenceParentPayload } from "@/lib/utils/matterFile";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { WandSparkles } from "lucide-react";

type MatterTemplateCategory = (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number];

const templateCategoryBySubItemType: Partial<
  Record<MatterSubItemType, MatterTemplateCategory>
> = {
  expense: "Expense",
  "incident-report": "Incident Report",
  insurance: "Insurance",
  lien: "Lien",
  "lost-wage": "Lost Wage",
  "medical-treatment": "Medical Treatment",
  "medical-treatment-appointment": "Medical Treatment Appointment",
  "medical-treatment-bill": "Medical Treatment Bill",
  "medical-treatment-plan": "Medical Treatment Plan",
  "medical-treatment-record": "Medical Treatment Record",
  "property-damage": "Property Damage",
  "settlement-counter": "Settlement Negotiation",
  "settlement-demand": "Settlement Negotiation",
  "settlement-offer": "Settlement Negotiation",
  "signature-request": "Signature Request",
  "trust-check": "Trust Check",
  vehicle: "Vehicle",
};

interface MatterSubItemGenerateDocumentContextMenuProps {
  matter: Matter;
  subItem: MatterDocumentSubItem;
}

export const MatterSubItemGenerateDocumentContextMenu = ({
  matter,
  subItem,
}: MatterSubItemGenerateDocumentContextMenuProps) => {
  const { hasActionPermission } = useUser();
  const { openGenerateDocumentModal } = useGlobalModal();
  const createMatterFileReference = useCreateMatterFileReference();
  const templateType = templateCategoryBySubItemType[subItem.type];
  const canGenerateDocument = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.create,
  );

  if (!templateType || !canGenerateDocument) return null;

  return (
    <>
      <ContextMenuItem
        onSelect={() => {
          openGenerateDocumentModal({
            matter,
            contacts: getMatterContactConnections(matter).map(
              (connection) => connection.contact,
            ),
            restrictTemplatesToType: true,
            subItem,
            templateType,
            afterGenerateFn: (files) => {
              files.forEach((file) => {
                createMatterFileReference.mutate({
                  matterId: matter.id,
                  fileId: file.id,
                  ...getMatterFileReferenceParentPayload(subItem),
                });
              });
            },
          });
        }}
      >
        <WandSparkles />
        Generate document
      </ContextMenuItem>
      <ContextMenuSeparator />
    </>
  );
};
